import { useCallback, useEffect, useState, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowsClockwise, CloudArrowDown, DownloadSimple, Lightning,
  Copy, Check, Link as LinkIcon, MagnifyingGlass, WarningCircle,
  ArrowSquareOut, Flame, Sparkle, ListNumbers,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL = "__ALL__";
const PAGE_SIZE = 50;

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const DEFAULT_LOOKBACK_DAYS = 90;

const fmtDt = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const iso2ToFlag = (iso2) => {
  if (!iso2 || iso2.length !== 2) return "";
  const base = 0x1F1E6;
  return String.fromCodePoint(
    base + iso2.toUpperCase().charCodeAt(0) - 65,
    base + iso2.toUpperCase().charCodeAt(1) - 65,
  );
};

const STATE_STYLES = {
  APPROVED: "bg-emerald-100 text-emerald-700",
  PENDING: "bg-amber-100 text-amber-700",
  REVERSED: "bg-rose-100 text-rose-700",
  approved: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  hold: "bg-indigo-100 text-indigo-700",
  rejected: "bg-rose-100 text-rose-700",
};

const FEED_TYPES = [
  { value: "database", label: "Stored Catalog" },
  { value: "top", label: "Top Offers" },
  { value: "new", label: "New Offers" },
  { value: "trending", label: "Trending" },
  { value: "popular", label: "Popular" },
  { value: "suggested", label: "Suggested" },
];

export default function AdminMaxBounty() {
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);

  const [startDate, setStartDate] = useState(daysAgo(DEFAULT_LOOKBACK_DAYS));
  const [endDate, setEndDate] = useState(today());
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, payout: 0, gross_sale_amount: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [syncingReport, setSyncingReport] = useState(false);
  const [syncingExpired, setSyncingExpired] = useState(false);
  const didFetch = useRef(false);

  // Active view tab: "conversions" | "offers" | "expired" | "callback"
  const [activeTab, setActiveTab] = useState("conversions");

  // Offers feed state
  const [feedType, setFeedType] = useState("database");
  const [campaigns, setCampaigns] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);

  // Expired campaigns state
  const [expiredList, setExpiredList] = useState([]);
  const [loadingExpired, setLoadingExpired] = useState(false);

  // Callback info state
  const [postbackInfo, setPostbackInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  // Tracking Link Modal State
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sub1, setSub1] = useState("");
  const [sub2, setSub2] = useState("");
  const [generatedLink, setGeneratedLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const { data } = await API.get("/admin/maxbounty/health");
      setHealth(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load MaxBounty health");
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        network: "maxbounty",
        // Network page = reconciliation view: list $0 conversions too.
        include_zero: "1",
      });
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (stateFilter) params.set("state", stateFilter);
      const { data } = await API.get(`/admin/actions?${params.toString()}`);
      let items = data.items || [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        items = items.filter((r) =>
          [r.action_id, r.campaign_name, r.advertiser_name, r.sub_id1, r.sub_id2]
            .some((v) => String(v || "").toLowerCase().includes(q)),
        );
      }
      setRows(items);
      setTotal(data.total || 0);
      setStats(data.stats || { total: 0, payout: 0, gross_sale_amount: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load MaxBounty conversions");
    } finally {
      setLoading(false);
    }
  }, [page, startDate, endDate, stateFilter, search]);

  const fetchFeed = useCallback(async () => {
    setLoadingFeed(true);
    try {
      const { data } = await API.get(`/admin/maxbounty/campaigns/${feedType}`);
      setCampaigns(data.campaigns || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch catalog feed");
    } finally {
      setLoadingFeed(false);
    }
  }, [feedType]);

  const fetchExpired = useCallback(async () => {
    setLoadingExpired(true);
    try {
      const { data } = await API.get("/admin/maxbounty/expired");
      setExpiredList(data.expired_campaigns || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch expired campaigns");
    } finally {
      setLoadingExpired(false);
    }
  }, []);

  const fetchPostbackInfo = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/maxbounty/postback-info");
      setPostbackInfo(data);
    } catch (err) {
      toast.error("Failed to load postback info");
    }
  }, []);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchHealth();
    fetchPostbackInfo();
  }, [fetchHealth, fetchPostbackInfo]);

  useEffect(() => {
    if (activeTab === "conversions") {
      const t = setTimeout(fetchRows, 250);
      return () => clearTimeout(t);
    }
  }, [fetchRows, activeTab]);

  useEffect(() => {
    if (activeTab === "offers") fetchFeed();
    if (activeTab === "expired") fetchExpired();
  }, [activeTab, fetchFeed, fetchExpired]);

  const handleSyncCatalog = async () => {
    setSyncingCatalog(true);
    try {
      const { data } = await API.post("/admin/maxbounty/sync-catalog");
      toast.success(
        `Catalog synced · ${data?.total_fetched ?? 0} fetched · ${data?.upserted_brands ?? 0} brands stored`,
      );
      fetchHealth();
      if (activeTab === "offers") fetchFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Catalog sync failed");
    } finally {
      setSyncingCatalog(false);
    }
  };

  const handleSyncExpired = async () => {
    setSyncingExpired(true);
    try {
      const { data } = await API.post("/admin/maxbounty/sync-expired");
      toast.success(
        `Expired sync complete · ${data?.deactivated_brands ?? 0} deactivated · ${data?.paused_assignments ?? 0} paused`,
      );
      fetchExpired();
      fetchHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Expired sync failed");
    } finally {
      setSyncingExpired(false);
    }
  };

  const downloadCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await API.get("/admin/actions", {
        params: {
          network: "maxbounty",
          include_zero: 1,
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(stateFilter ? { state: stateFilter } : {}),
        },
      });
      const all = Array.isArray(data) ? data : (data.items || []);
      const headers = [
        "event_date", "action_id", "campaign", "advertiser", "publisher", "country",
        "sub_id1", "sub_id2", "state", "amount", "payout", "currency",
      ];
      const lines = [headers.join(",")];
      for (const r of all) {
        lines.push([
          r.event_date, r.action_id, r.campaign_name, r.advertiser_name,
          r.publisher_email, r.brand_country, r.sub_id1, r.sub_id2, r.state || r.status,
          r.amount, r.payout, r.currency,
        ].map((v) => {
          const s = v == null ? "" : String(v);
          return s.includes(",") ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `maxbounty-conversions-${startDate}-to-${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleGenerateLink = async (e) => {
    e.preventDefault();
    if (!selectedCampaign) return;
    setGeneratingLink(true);
    try {
      const { data } = await API.post("/admin/maxbounty/tracking-link", {
        campaign_id: Number(selectedCampaign.campaign_id),
        sub1,
        sub2,
      });
      setGeneratedLink(data["tracking-link"] || "");
      toast.success("Tracking link generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyPostbackUrl = () => {
    if (!postbackInfo?.url_template) return;
    navigator.clipboard.writeText(postbackInfo.url_template);
    setCopied(true);
    toast.success("Callback URL template copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const configured = health?.configured;

  const filteredCampaigns = campaigns.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(c.campaign_id).includes(q) ||
      (c.name || "").toLowerCase().includes(q) ||
      (c.rate_type || "").toLowerCase().includes(q) ||
      (c.categories || []).some((cat) => cat.toLowerCase().includes(q))
    );
  });

  return (
    <div className="p-8 space-y-6" data-testid="admin-maxbounty-page">
      {/* Header Banner */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 font-bold tracking-[0.2em] uppercase">NETWORK</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">MaxBounty</h1>
          <p className="text-sm text-slate-500 mt-1">
            MaxBounty (MB1) Affiliate API — catalog feed sync + conversion callback/report pull into <code className="bg-slate-100 px-1 rounded text-[11px]">actions</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={downloadCsv}
            disabled={exporting || total === 0}
            data-testid="maxbounty-export"
          >
            <DownloadSimple size={14} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { fetchHealth(); fetchRows(); }} data-testid="maxbounty-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Configuration Alert */}
      {!loadingHealth && !configured ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="maxbounty-not-configured">
          <strong>MaxBounty API not configured.</strong> Add <code className="bg-amber-100 px-1 rounded">MAXBOUNTY_EMAIL</code> and{" "}
          <code className="bg-amber-100 px-1 rounded">MAXBOUNTY_PASSWORD</code> to <code className="bg-amber-100 px-1 rounded">backend/.env</code>, then restart the backend.
        </div>
      ) : null}

      {/* Health + Sync Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Catalog</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">
            {(health?.brands_active ?? 0).toLocaleString()} active
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            {(health?.brands_total ?? 0).toLocaleString()} total brands
          </div>
          <Button
            variant="outline" size="sm" className="mt-3"
            onClick={handleSyncCatalog}
            disabled={syncingCatalog || !configured}
            data-testid="maxbounty-sync-catalog"
          >
            <ArrowsClockwise size={14} className="mr-1.5" />
            {syncingCatalog ? "Syncing…" : "Sync catalog"}
          </Button>
        </div>

        <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
            Pull conversions / catalog from MaxBounty API
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Start</Label>
              <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} data-testid="maxbounty-start" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">End</Label>
              <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} data-testid="maxbounty-end" />
            </div>
            <Button
              onClick={handleSyncCatalog}
              disabled={syncingCatalog || !configured}
              className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
              data-testid="maxbounty-sync-report"
            >
              <CloudArrowDown size={14} className="mr-1.5" />
              {syncingCatalog ? "Pulling…" : "Sync catalog"}
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            Last report sync: {health?.last_report?.completed_at || health?.last_report?.started_at ? fmtDt(health.last_report.completed_at || health.last_report.started_at) : "never"} ·{" "}
            {(health?.actions_total ?? 0).toLocaleString()} conversions in DB
          </p>
        </div>
      </div>

      {/* Mode / Tab Selector */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("conversions")}
          className={`pb-3 px-4 font-semibold text-sm transition-colors border-b-2 ${
            activeTab === "conversions"
              ? "border-[#002FA7] text-[#002FA7]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Conversions ({total})
        </button>
        <button
          onClick={() => setActiveTab("offers")}
          className={`pb-3 px-4 font-semibold text-sm transition-colors border-b-2 ${
            activeTab === "offers"
              ? "border-[#002FA7] text-[#002FA7]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Offers Catalog Feed ({(health?.brands_total ?? 0).toLocaleString()})
        </button>
        <button
          onClick={() => setActiveTab("expired")}
          className={`pb-3 px-4 font-semibold text-sm transition-colors border-b-2 ${
            activeTab === "expired"
              ? "border-[#002FA7] text-[#002FA7]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Expired Offers ({expiredList.length})
        </button>
        <button
          onClick={() => setActiveTab("callback")}
          className={`pb-3 px-4 font-semibold text-sm transition-colors border-b-2 ${
            activeTab === "callback"
              ? "border-[#002FA7] text-[#002FA7]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          S2S Callback Setup
        </button>
      </div>

      {/* TAB 1: Conversions */}
      {activeTab === "conversions" && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="maxbounty-filters">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</Label>
              <Select value={stateFilter || ALL} onValueChange={(v) => { setStateFilter(v === ALL ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="maxbounty-state"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All statuses</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="PENDING">Pending / Hold</SelectItem>
                  <SelectItem value="REVERSED">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Search (this page)</Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="action ID / brand / sub_id"
                data-testid="maxbounty-search"
              />
            </div>
          </div>

          {/* Period totals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm" data-testid="maxbounty-rollup">
            <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Conversions (period)</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">{(stats.total ?? total).toLocaleString()}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{startDate} → {endDate}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission (period)</div>
              <div className="font-heading text-xl font-bold text-[#002FA7] mt-1">${Number(stats.payout || 0).toFixed(2)}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">USD net (excludes reversed)</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sales (period)</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">${Number(stats.gross_sale_amount || 0).toFixed(2)}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">USD gross</div>
            </div>
          </div>

          {/* Table */}
          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Date</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Brand</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Action ID</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sub IDs</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Country</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sales</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-slate-500 text-sm">
                      {configured
                        ? "No conversions recorded for this window — set up URL Callback in MaxBounty Dashboard."
                        : "Configure MaxBounty API credentials first."}
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.action_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`maxbounty-row-${r.action_id}`}>
                    <td className="px-3 py-2 text-[11px] text-slate-700 whitespace-nowrap">{fmtDt(r.event_date || r.created_at)}</td>
                    <td className="px-3 py-2 text-[11px] font-semibold text-slate-800">{r.campaign_name || r.advertiser_name || "—"}</td>
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-600">{r.action_id}</td>
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-600">
                      {[r.sub_id1, r.sub_id2].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-700">{r.publisher_email || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-600">
                      {r.brand_country ? (
                        <span className="inline-flex items-center gap-1">
                          <span>{iso2ToFlag(r.brand_country)}</span>
                          <span className="font-mono">{r.brand_country}</span>
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-[11px] font-mono text-right">${Number(r.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-[11px] font-mono text-right text-[#002FA7] font-bold">${Number(r.payout || 0).toFixed(2)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${STATE_STYLES[r.state || r.status] || "bg-slate-100 text-slate-600"}`}>
                        {r.state || r.status || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <div>
              {total.toLocaleString()} conversion{total === 1 ? "" : "s"} · page <span className="font-mono font-bold">{page}</span> / <span className="font-mono">{totalPages}</span>
            </div>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Prev</Button>
              <Button variant="outline" size="sm" disabled={loading || page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Offers Catalog Feed */}
      {activeTab === "offers" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-50 p-4 rounded-md border border-slate-200">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Feed Category:</Label>
              <Select value={feedType} onValueChange={setFeedType}>
                <SelectTrigger className="w-[180px] bg-white">
                  <SelectValue placeholder="Select Feed" />
                </SelectTrigger>
                <SelectContent>
                  {FEED_TYPES.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchFeed}
                disabled={loadingFeed}
              >
                <ArrowsClockwise size={14} className={`mr-1.5 ${loadingFeed ? "animate-spin" : ""}`} />
                Reload Feed
              </Button>
            </div>

            <div className="w-full sm:w-72">
              <Input
                placeholder="Search offers by ID, name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-white"
              />
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Offer ID</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Campaign Name</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Payout Rate</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">EPC</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Categories</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingFeed ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400 text-sm">Loading feed offers…</td></tr>
                ) : filteredCampaigns.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400 text-sm">No campaigns found in feed.</td></tr>
                ) : (
                  filteredCampaigns.map((c) => (
                    <tr key={c.campaign_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono font-bold text-slate-900 text-[11px]">#{c.campaign_id}</td>
                      <td className="px-3 py-2 text-[11px] font-semibold text-slate-800">
                        <div className="flex items-center gap-2">
                          {c.thumbnail && (
                            <img src={c.thumbnail} alt="" className="w-7 h-7 rounded border border-slate-200 object-cover" />
                          )}
                          <div>
                            <span>{c.name}</span>
                            {c.landing_page_sample && (
                              <a
                                href={c.landing_page_sample}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 inline-flex items-center text-[10px] text-blue-600 hover:underline"
                              >
                                Preview <ArrowSquareOut size={10} className="ml-0.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[11px] font-bold text-emerald-700">
                        {c.rate_text || (c.rate ? `$${c.rate} (${c.rate_type || ""})` : "—")}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                        {c.epc ? `$${Number(c.epc).toFixed(3)}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-[11px]">
                        <div className="flex flex-wrap gap-1">
                          {(c.categories || []).map((cat, idx) => (
                            <span key={idx} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700">
                              {cat}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCampaign(c);
                            setLinkModalOpen(true);
                            setGeneratedLink("");
                          }}
                          className="text-[11px] h-7 px-2"
                        >
                          <LinkIcon size={12} className="mr-1" /> Get Link
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: Expired Offers */}
      {activeTab === "expired" && (
        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-amber-900 text-sm">Expired Campaign Cascade Management</h3>
              <p className="text-xs text-amber-700 mt-0.5">
                Automatically deactivates expired MaxBounty brands and pauses active publisher assignments.
              </p>
            </div>
            <Button
              onClick={handleSyncExpired}
              disabled={syncingExpired}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
            >
              {syncingExpired ? "Deactivating..." : "Run Expired Sync"}
            </Button>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Campaign ID</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Campaign Name</th>
                  <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status Date</th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingExpired ? (
                  <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-400 text-sm">Checking expired list…</td></tr>
                ) : expiredList.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-400 text-sm">No expired campaigns reported in the last 30 days.</td></tr>
                ) : (
                  expiredList.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono font-bold text-slate-900 text-[11px]">#{item.campaign_id || item}</td>
                      <td className="px-3 py-2 text-[11px] font-medium text-slate-800">{item.name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{item.status_date || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700">
                          Inactive
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: S2S Callback Setup */}
      {activeTab === "callback" && postbackInfo && (
        <div className="space-y-6">
          <div className="border border-slate-200 bg-white p-6 rounded-md space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-slate-900">MaxBounty Global URL Callback Template</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Set this URL in MaxBounty Dashboard → <strong>Account Settings → Callback / Postback → URL Callback</strong>.
                </p>
              </div>
              <Button onClick={copyPostbackUrl} className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white text-xs">
                {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                {copied ? "Copied!" : "Copy Callback URL"}
              </Button>
            </div>

            <div className="p-3 bg-slate-900 text-emerald-400 font-mono text-xs rounded overflow-x-auto select-all">
              {postbackInfo.url_template}
            </div>
          </div>

          <div className="border border-slate-200 bg-white p-6 rounded-md space-y-3">
            <h3 className="text-sm font-bold text-slate-900">MaxBounty Placeholder Legend</h3>
            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">MaxBounty Placeholder</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Mapped Parameter</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.entries(postbackInfo.placeholder_mapping || {}).map(([ph, desc]) => (
                    <tr key={ph} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono font-bold text-blue-700 text-[11px]">{ph}</td>
                      <td className="px-3 py-2 font-mono text-slate-800 text-[11px]">{ph === "#S1#" ? "rid" : ph === "#S2#" ? "sub_id1" : ph === "#CONVERSION_ID#" ? "action_id" : ph === "#OFFID#" ? "campaign_id" : ph === "#RATE#" ? "payout" : ph === "#SALE#" ? "amount" : "ip"}</td>
                      <td className="px-3 py-2 text-slate-600 text-[11px]">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Link Modal */}
      {linkModalOpen && selectedCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="bg-white rounded-md border border-slate-200 max-w-lg w-full p-6 space-y-4 shadow-lg">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-sm">
                Generate Link: #{selectedCampaign.campaign_id} {selectedCampaign.name}
              </h3>
              <button onClick={() => setLinkModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold text-sm">
                ✕
              </button>
            </div>

            <form onSubmit={handleGenerateLink} className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">sub1 (rid / click ID)</Label>
                  <Input placeholder="e.g. click_991823" value={sub1} onChange={(e) => setSub1(e.target.value)} className="mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">sub2 (Publisher SubID)</Label>
                  <Input placeholder="e.g. pub_sub_10" value={sub2} onChange={(e) => setSub2(e.target.value)} className="mt-1 font-mono text-xs" />
                </div>
              </div>

              <Button type="submit" disabled={generatingLink} className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white text-xs">
                {generatingLink ? "Generating…" : "Generate Tracking Link"}
              </Button>
            </form>

            {generatedLink && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Affiliate Tracking Link:</Label>
                <div className="font-mono text-xs text-slate-900 break-all select-all p-2 bg-white rounded border border-slate-200">
                  {generatedLink}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedLink);
                    toast.success("Link copied!");
                  }}
                  className="w-full text-xs"
                >
                  <Copy size={12} className="mr-1" /> Copy Link
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
